import React, { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaCalendar, FaClock, FaPhone, FaEnvelope, FaUsers, FaMapMarkerAlt } from 'react-icons/fa';
import { RiArrowRightUpLine } from 'react-icons/ri';
import './ReservationForm.css';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import { createReservation } from '../../api/reservationApi';
import LanguageContext from '../../context/LanguageContext';
import CustomDropdown from '../UI/CustomDropdown/CustomDropdown';
import DatePicker from '../DatePicker/DatePicker';
import TimePicker from '../TimePicker/TimePicker';
import { hasPublicAddress } from '../../utils/formatPublicAddress.js';
import { formatShiftRange, weekdayLabel } from '../../utils/openHoursLabel.js';

/** Bookable hours for reservations (24h clock). Edit these to change which hours appear in the TimePicker. */
const RESERVATION_TIME_START_HOUR = 11;
const RESERVATION_TIME_END_HOUR = 22;

const ReservationForm = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const { publicSettings } = usePublicSettings();
    const formRef = useRef(null);
    const [step, setStep] = useState(0);

    const initialFormState = {
        name: '', email: '', phone: '',
        date: '', time: '', guests: '2', specialRequests: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [focused, setFocused] = useState(null);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const getMissingRequiredFields = () => {
        const missing = [];
        if (!String(formData.name || '').trim()) missing.push('name');
        if (!String(formData.email || '').trim()) missing.push('email');
        if (!String(formData.phone || '').trim()) missing.push('phone');
        if (!String(formData.date || '').trim()) missing.push('date');
        if (!String(formData.time || '').trim()) missing.push('time');
        const guests = Number(formData.guests);
        if (!Number.isFinite(guests) || guests < 1) missing.push('guests');
        return missing;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (step !== 2) return;
        const missing = getMissingRequiredFields();
        if (missing.length > 0) {
            setError(t('reservation.validationRequired'));
            setStep(0);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await createReservation({
                name: String(formData.name).trim(),
                email: String(formData.email).trim(),
                phone: String(formData.phone).trim(),
                date: String(formData.date).trim(),
                time: String(formData.time).trim(),
                guests: Number(formData.guests),
                specialRequests: String(formData.specialRequests || '').trim(),
            });
            setSubmitted(true);
            setStep(0);
            setTimeout(() => {
                setSubmitted(false);
                setFormData(initialFormState);
            }, 4000);
        } catch (err) {
            if (err.message?.includes('429'))           setError(t('reservation.tooManyAttempts'));
            else if (err.message?.includes('Network'))  setError(t('reservation.networkError'));
            else                                         setError(t('reservation.submitError'));
        } finally {
            setLoading(false);
        }
    };

    const goToReview = () => {
        if (!formRef.current?.checkValidity()) {
            formRef.current?.reportValidity();
            return;
        }
        const missing = getMissingRequiredFields();
        if (missing.length > 0) {
            setError(t('reservation.validationRequired'));
            return;
        }
        setError(null);
        setStep(1);
    };

    const getMinDate = () => new Date().toISOString().split('T')[0];

    const guestOptions = useMemo(
        () =>
            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
                value: String(n),
                label: `${n} ${n === 1 ? t('reservation.guest') : t('reservation.guestsPlural')}`,
            })),
        [t]
    );

    const formatDisplayDate = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(`${iso}T12:00:00`).toLocaleDateString(i18n.language, { dateStyle: 'long' });
        } catch {
            return iso;
        }
    };

    const formatDisplayTime = (hm) => {
        if (!hm) return '—';
        const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hm);
        if (!match) return hm;
        const h = parseInt(match[1], 10);
        const m = parseInt(match[2], 10);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        return d.toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' });
    };

    const guestsSummary = () => {
        const n = parseInt(formData.guests, 10) || 0;
        return `${n} ${n === 1 ? t('reservation.guest') : t('reservation.guestsPlural')}`;
    };

    const stepClass = (index) =>
        ['rf-step', step === index && 'rf-step--active', step > index && 'rf-step--completed'].filter(Boolean).join(' ');

    const infoItems = [
        {
            icon: <FaPhone />,
            label: t('reservation.callUs'),
            value: publicSettings?.phoneNumber || t('contact.phoneNotConfigured'),
        },
        {
            icon: <FaEnvelope />,
            label: t('reservation.emailUs'),
            value: publicSettings?.email || t('contact.emailNotConfigured'),
        },
        {
            icon: <FaMapMarkerAlt />,
            label: t('reservation.address'),
            value: hasPublicAddress(publicSettings?.address) ? (
                <>
                    {publicSettings.address.street && <>{publicSettings.address.street}<br /></>}
                    {(publicSettings.address.city || publicSettings.address.state || publicSettings.address.zipCode) && (
                        <>
                            {publicSettings.address.city}
                            {publicSettings.address.state && `, ${publicSettings.address.state}`}
                            {publicSettings.address.zipCode && ` ${publicSettings.address.zipCode}`}
                            <br />
                        </>
                    )}
                    {publicSettings.address.country}
                </>
            ) : t('contact.addressNotConfigured'),
        },
    ];

    return (
        <section className="rf-root" dir={dir()}>
            {/* ── Page header ── */}
            <div className="container rf-header">
                <span className="rf-eyebrow">{t('reservation.infoTitle')}</span>
                <h2 className="rf-page-title">{t('reservation.infoTitle')}</h2>
                <p className="rf-page-desc">{t('reservation.infoDescription')}</p>
            </div>

            <div className="container rf-layout">

                {/* ══ Left panel — info ══ */}
                <aside className="rf-info">
                    <div className="rf-info-inner">
                        <p className="rf-info-tagline">{t('reservation.note')}</p>

                        {/* Contact cards */}
                        <ul className="rf-contact-list">
                            {infoItems.map((item, i) => (
                                <li key={i} className="rf-contact-item">
                                    <div className="rf-contact-ico">{item.icon}</div>
                                    <div>
                                        <span className="rf-contact-label">{item.label}</span>
                                        <p className="rf-contact-val">{item.value}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {/* Hours */}
                        <div className="rf-hours-block">
                            <div className="rf-hours-header">
                                <FaClock className="rf-hours-ico" />
                                <span>{t('reservation.openHours')}</span>
                            </div>
                            <ul className="rf-hours-list">
                                {publicSettings?.openHours ? (
                                    publicSettings.openHours.map(d => {
                                        const shifts = d.shifts
                                            .map((s) => formatShiftRange(s.openTime, s.closeTime))
                                            .join(', ');
                                        return (
                                            <li key={d.day} className={`rf-hours-row ${d.isOpen ? '' : 'rf-hours-row--closed'}`}>
                                                <span className="rf-hours-day">{weekdayLabel(d.day, t)}</span>
                                                <span className="rf-hours-time" dir={d.isOpen ? 'ltr' : undefined}>
                                                    {d.isOpen ? shifts : t('reservation.closed')}
                                                </span>
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li className="rf-hours-row"><span>{t('reservation.hours')}</span></li>
                                )}
                            </ul>
                        </div>

                        {/* Special notices */}
                        {(publicSettings?.specialNotice || publicSettings?.isTemporarilyClosed) && (
                            <div className={`rf-notice ${publicSettings?.isTemporarilyClosed ? 'rf-notice--closed' : ''}`}>
                                <div className="rf-notice-dot" aria-hidden="true" />
                                <div>
                                    <p className="rf-notice-title">
                                        {publicSettings?.isTemporarilyClosed
                                            ? t('reservation.temporarilyClosed')
                                            : t('reservation.importantNotice')}
                                    </p>
                                    <p className="rf-notice-msg">
                                        {publicSettings?.isTemporarilyClosed
                                            ? (publicSettings.temporaryClosureMessage || t('reservation.temporarilyClosedMessage'))
                                            : publicSettings.specialNotice}
                                    </p>
                                </div>
                            </div>
                        )}

                        <blockquote className="rf-quote" aria-hidden="true">
                            {t('reservation.decorQuote')}
                        </blockquote>
                    </div>
                </aside>

                {/* ══ Right panel — form ══ */}
                <div className="rf-form-panel">
                    {submitted ? (
                        <div className="rf-success">
                            <div className="rf-success-ring" aria-hidden="true">
                                <svg viewBox="0 0 60 60" fill="none">
                                    <circle cx="30" cy="30" r="28" stroke="currentColor" strokeWidth="2" strokeDasharray="176" strokeDashoffset="0" className="rf-success-circle"/>
                                    <path d="M18 30l9 9 15-15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="rf-success-check"/>
                                </svg>
                            </div>
                            <h3 className="rf-success-title">{t('reservation.successTitle')}</h3>
                            <p className="rf-success-msg">{t('reservation.successMessage')}</p>
                        </div>
                    ) : (
                        <form ref={formRef} className="rf-form" onSubmit={handleSubmit} noValidate>
                            <div className="rf-form-head">
                                <h3 className="rf-form-title">
                                    {step === 0 && t('reservation.formTitle')}
                                    {step === 1 && t('reservation.reviewTitle')}
                                    {step === 2 && t('reservation.confirmTitle')}
                                </h3>
                                <p className="rf-form-sub">
                                    {step === 0 && t('reservation.formSubStepPersonal')}
                                    {step === 1 && t('reservation.reviewSubtitle')}
                                    {step === 2 && t('reservation.confirmSubtitle')}
                                </p>
                            </div>

                            <nav className="rf-steps" aria-label={t('reservation.stepsAriaLabel')}>
                                <span className={stepClass(0)} aria-current={step === 0 ? 'step' : undefined}>
                                    {t('reservation.stepPersonal')}
                                </span>
                                <span className={`rf-step-line ${step >= 1 ? 'rf-step-line--complete' : ''}`} aria-hidden="true" />
                                <span className={stepClass(1)} aria-current={step === 1 ? 'step' : undefined}>
                                    {t('reservation.stepReview')}
                                </span>
                                <span className={`rf-step-line ${step >= 2 ? 'rf-step-line--complete' : ''}`} aria-hidden="true" />
                                <span className={stepClass(2)} aria-current={step === 2 ? 'step' : undefined}>
                                    {t('reservation.stepConfirm')}
                                </span>
                            </nav>

                            {step === 0 && (
                            <>
                            {error && (
                                <div className="rf-error" role="alert">
                                    <span className="rf-error-dot" aria-hidden="true" />
                                    {error}
                                </div>
                            )}
                            {/* Row 1 */}
                            <div className="rf-row">
                                <div className={`rf-group ${focused === 'name' ? 'rf-group--focus' : ''} ${formData.name ? 'rf-group--filled' : ''}`}>
                                    <label className="rf-label" htmlFor="rf-name">
                                        <FaUser className="rf-lbl-ico" />
                                        {t('reservation.name')}
                                    </label>
                                    <input
                                        id="rf-name" type="text" name="name"
                                        placeholder={t('reservation.namePlaceholder')}
                                        className="rf-input"
                                        value={formData.name} onChange={handleChange}
                                        onFocus={() => setFocused('name')} onBlur={() => setFocused(null)}
                                        required
                                    />
                                </div>

                                <div className={`rf-group ${focused === 'email' ? 'rf-group--focus' : ''} ${formData.email ? 'rf-group--filled' : ''}`}>
                                    <label className="rf-label" htmlFor="rf-email">
                                        <FaEnvelope className="rf-lbl-ico" />
                                        {t('reservation.email')}
                                    </label>
                                    <input
                                        id="rf-email" type="email" name="email"
                                        placeholder={t('reservation.emailPlaceholder')}
                                        className="rf-input"
                                        value={formData.email} onChange={handleChange}
                                        onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Row 2 */}
                            <div className="rf-row">
                                <div className={`rf-group ${focused === 'phone' ? 'rf-group--focus' : ''} ${formData.phone ? 'rf-group--filled' : ''}`}>
                                    <label className="rf-label" htmlFor="rf-phone">
                                        <FaPhone className="rf-lbl-ico" />
                                        {t('reservation.phone')}
                                    </label>
                                    <input
                                        id="rf-phone" type="tel" name="phone"
                                        placeholder={t('reservation.phonePlaceholder')}
                                        className="rf-input"
                                        value={formData.phone} onChange={handleChange}
                                        onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)}
                                        required
                                    />
                                </div>

                                <div className={`rf-group ${formData.guests ? 'rf-group--filled' : ''}`}>
                                    <label className="rf-label" id="rf-guests-label" htmlFor="rf-guests">
                                        <FaUsers className="rf-lbl-ico" />
                                        {t('reservation.guests')}
                                    </label>
                                    <CustomDropdown
                                        id="rf-guests"
                                        ariaLabelledBy="rf-guests-label"
                                        className="rf-guests-dropdown"
                                        options={guestOptions}
                                        value={formData.guests}
                                        onChange={(val) => setFormData((prev) => ({ ...prev, guests: val }))}
                                        placeholder={t('reservation.guests')}
                                    />
                                </div>
                            </div>

                            {/* Row 3 */}
                            <div className="rf-row">
                                <div className={`rf-group ${formData.date ? 'rf-group--filled' : ''}`}>
                                    <label className="rf-label" id="rf-date-label" htmlFor="rf-date">
                                        <FaCalendar className="rf-lbl-ico" />
                                        {t('reservation.date')}
                                    </label>
                                    <DatePicker
                                        id="rf-date"
                                        name="date"
                                        value={formData.date}
                                        onChange={(iso) => setFormData((prev) => ({ ...prev, date: iso }))}
                                        minDate={getMinDate()}
                                        locale={i18n.language}
                                        ariaLabelledBy="rf-date-label"
                                        dir={dir()}
                                        required
                                        className="rf-date-picker"
                                    />
                                </div>

                                <div className={`rf-group ${formData.time ? 'rf-group--filled' : ''}`}>
                                    <label className="rf-label" id="rf-time-label" htmlFor="rf-time">
                                        <FaClock className="rf-lbl-ico" />
                                        {t('reservation.time')}
                                    </label>
                                    <TimePicker
                                        id="rf-time"
                                        name="time"
                                        value={formData.time}
                                        onChange={(hm) => setFormData((prev) => ({ ...prev, time: hm }))}
                                        locale={i18n.language}
                                        ariaLabelledBy="rf-time-label"
                                        dir={dir()}
                                        required
                                        minuteStep={15}
                                        startHour={RESERVATION_TIME_START_HOUR}
                                        endHour={RESERVATION_TIME_END_HOUR}
                                        className="rf-time-picker"
                                    />
                                </div>
                            </div>

                            {/* Special requests */}
                            <div className={`rf-group rf-group--full ${focused === 'sr' ? 'rf-group--focus' : ''} ${formData.specialRequests ? 'rf-group--filled' : ''}`}>
                                <label className="rf-label" htmlFor="rf-sr">
                                    {t('reservation.specialRequests')}
                                </label>
                                <textarea
                                    id="rf-sr" name="specialRequests"
                                    placeholder={t('reservation.specialRequestsPlaceholder')}
                                    className="rf-input rf-textarea"
                                    value={formData.specialRequests} onChange={handleChange}
                                    onFocus={() => setFocused('sr')} onBlur={() => setFocused(null)}
                                    rows={4}
                                />
                            </div>

                            <div className="rf-actions rf-actions--end">
                                <button type="button" className="rf-submit" onClick={goToReview}>
                                    <span>{t('reservation.continueToReview')}</span>
                                    <span className="rf-submit-ico" aria-hidden="true">
                                        <RiArrowRightUpLine />
                                    </span>
                                    <span className="rf-submit-shine" aria-hidden="true" />
                                </button>
                            </div>
                            </>
                            )}

                            {step === 1 && (
                                <>
                                    <dl className="rf-review">
                                        <div className="rf-review-row">
                                            <dt>{t('reservation.name')}</dt>
                                            <dd>{formData.name}</dd>
                                        </div>
                                        <div className="rf-review-row">
                                            <dt>{t('reservation.email')}</dt>
                                            <dd>{formData.email}</dd>
                                        </div>
                                        <div className="rf-review-row">
                                            <dt>{t('reservation.phone')}</dt>
                                            <dd>{formData.phone}</dd>
                                        </div>
                                        <div className="rf-review-row">
                                            <dt>{t('reservation.guests')}</dt>
                                            <dd>{guestsSummary()}</dd>
                                        </div>
                                        <div className="rf-review-row">
                                            <dt>{t('reservation.date')}</dt>
                                            <dd>{formatDisplayDate(formData.date)}</dd>
                                        </div>
                                        <div className="rf-review-row">
                                            <dt>{t('reservation.time')}</dt>
                                            <dd>{formatDisplayTime(formData.time)}</dd>
                                        </div>
                                        <div className="rf-review-row rf-review-row--block">
                                            <dt>{t('reservation.specialRequests')}</dt>
                                            <dd>{formData.specialRequests?.trim() ? formData.specialRequests : t('reservation.none')}</dd>
                                        </div>
                                    </dl>
                                    <div className="rf-actions rf-actions--split">
                                        <button type="button" className="rf-btn-secondary" onClick={() => { setError(null); setStep(0); }}>
                                            {t('reservation.back')}
                                        </button>
                                        <button type="button" className="rf-submit rf-submit--inline" onClick={() => { setError(null); setStep(2); }}>
                                            <span>{t('reservation.proceedToConfirm')}</span>
                                            <span className="rf-submit-ico" aria-hidden="true">
                                                <RiArrowRightUpLine />
                                            </span>
                                            <span className="rf-submit-shine" aria-hidden="true" />
                                        </button>
                                    </div>
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    {error && (
                                        <div className="rf-error" role="alert">
                                            <span className="rf-error-dot" aria-hidden="true" />
                                            {error}
                                        </div>
                                    )}
                                    <div className="rf-actions rf-actions--split">
                                        <button type="button" className="rf-btn-secondary" onClick={() => { setError(null); setStep(1); }}>
                                            {t('reservation.back')}
                                        </button>
                                        <button
                                            type="submit"
                                            className={`rf-submit rf-submit--inline ${loading ? 'rf-submit--loading' : ''}`}
                                            disabled={loading}
                                        >
                                            <span>
                                                {loading ? t('reservation.loading') : t('reservation.submit')}
                                            </span>
                                            <span className="rf-submit-ico" aria-hidden="true">
                                                <RiArrowRightUpLine />
                                            </span>
                                            <span className="rf-submit-shine" aria-hidden="true" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ReservationForm;